import { useState, useEffect, useCallback, useRef } from 'react';

type VisibilityState = 'visible' | 'hidden' | 'prerender' | 'unloaded';

interface UsePageVisibilityOptions {
  /** Callback executado quando a página fica visível (usuário volta para a aba) */
  onVisible?: () => void;
  /** Callback executado quando a página fica oculta (usuário sai da aba) */
  onHidden?: () => void;
  /** Se true, reconecta automaticamente quando a página volta a ficar visível */
  autoReconnect?: boolean;
  /** Tempo de debounce em ms para evitar múltiplas chamadas consecutivas */
  debounceMs?: number;
}

/**
 * Hook que detecta mudanças na visibilidade da página/janela do navegador.
 * Útil para reconectar conexões Realtime, pausar timers, etc.
 */
export const usePageVisibility = (options: UsePageVisibilityOptions = {}) => {
  const {
    onVisible,
    onHidden,
    autoReconnect = false,
    debounceMs = 100
  } = options;

  const [isVisible, setIsVisible] = useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
  );
  const [visibilityState, setVisibilityState] = useState<VisibilityState>(
    typeof document !== 'undefined' ? document.visibilityState : 'visible'
  );
  
  const lastVisibleTime = useRef<number>(Date.now());
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const wasHidden = useRef<boolean>(false);

  const handleVisibilityChange = useCallback(() => {
    const currentState = document.visibilityState;
    setVisibilityState(currentState);
    const nowVisible = currentState === 'visible';
    setIsVisible(nowVisible);

    // Debounce para evitar múltiplas chamadas
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (nowVisible) {
        // Página ficou visível
        const timeAway = Date.now() - lastVisibleTime.current;
        console.log(`[usePageVisibility] Página voltou a ficar visível. Tempo fora: ${timeAway}ms`);
        
        if (wasHidden.current && onVisible) {
          onVisible();
        }
        wasHidden.current = false;
      } else {
        // Página ficou oculta
        console.log('[usePageVisibility] Página ficou oculta');
        lastVisibleTime.current = Date.now();
        wasHidden.current = true;
        
        if (onHidden) {
          onHidden();
        }
      }
    }, debounceMs);
  }, [onVisible, onHidden, debounceMs]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [handleVisibilityChange]);

  // Retorna funções úteis
  return {
    isVisible,
    visibilityState,
    /** Tempo que a página ficou oculta (em ms), 0 se estiver visível */
    timeAway: isVisible ? 0 : Date.now() - lastVisibleTime.current,
    /** Se a página estuvo oculta desde o último check */
    wasAway: wasHidden.current && isVisible,
  };
};

/**
 * Versão simples do hook que só retorna se a página está visível
 */
export const useIsPageVisible = () => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleChange);
    return () => document.removeEventListener('visibilitychange', handleChange);
  }, []);

  return isVisible;
};

export default usePageVisibility;
